// @ts-nocheck
import * as THREE from "three";

import React, { useEffect, useMemo, useRef, Suspense, useState } from "react";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader";
import { Canvas, useThree, useLoader, useFrame } from "@react-three/fiber";
import {
  Stats,
  PerformanceMonitor,
  useHelper,
  // Environment,
} from "@react-three/drei";

import { Backdrop, Box, CircularProgress, Grid } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Slider, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

import { useControls } from "leva";


import BuilderSelect from "@/store/builderStoreSelector";

// Add these exports at the top of RoomViewUtils.tsx
export const TRANSITION_DURATION = "300ms";
export const TRANSITION_TIMING = "cubic-bezier(0.4, 0, 0.2, 1)";
export const TRANSITION_PROPERTIES = `all ${TRANSITION_DURATION} ${TRANSITION_TIMING}`;
export const CANVAS_SCALE_FACTOR = 0.8;
export const CANVAS_TRANSLATE_Y = "-45px";
export const CANVAS_Z_INDEX = 10;
export const OVERLAY_Z_INDEX = 9;
export const MAX_SAMPLES = 64;
export const SAMPLES_THRESHOLD = 64;

interface DonutProgressProps {
  value?: number;
  max?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  innerColor?: string;
  currentCount?: number;
  finalCount?: number;
  label?: string;
}


const Environment = ({ files, background = false, intensity = 1 }) => {
  const { scene } = useThree();
  const loader = useMemo(() => new RGBELoader(), []);

  useEffect(() => {
    if (!files) return;

    // Load the HDR file
    loader.load(files, (texture) => {
      // Ensure texture is equirectangular and set mapping
      texture.mapping = THREE.EquirectangularReflectionMapping;

      // Apply texture as environment map
      scene.environment = texture;

      // Optionally set as background
      if (background) {
        scene.background = texture;
      }

      // Apply intensity
      scene.environmentIntensity = intensity;

      // Cleanup on unmount or when files change
      return () => {
        scene.environment = null;
        if (background) {
          scene.background = null;
        }
        texture.dispose();
      };
    }, (xhr) => {
      // Optional: Log loading progress
      console.log((xhr.loaded / xhr.total * 100) + '% loaded');
    }, (error) => {
      console.error('Error loading HDR:', error);
    });

    // Cleanup on intensity or background change
    return () => {
      if (scene.environment) {
        scene.environment = null;
      }
      if (background && scene.background) {
        scene.background = null;
      }
    };
  }, [files, background, intensity, scene, loader]);

  // Update intensity if it changes without reloading the texture
  useEffect(() => {
    scene.environmentIntensity = intensity;
  }, [intensity, scene]);

  return null;
};


export const DonutProgress: React.FC<DonutProgressProps> = ({
  value = 75,
  max = 100,
  size = 80,
  strokeWidth = 8,
  color = "#8e44ec",
  innerColor = "#eff6ff",
  currentCount = 75,
  finalCount = 100,
  label = "Samples",
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - value / 100);
  const isProcessing = label === "Processing Room"; // Check if we're in processing stage

  return (
    <div
      className="flex flex-col items-center"
      style={{
        position: "absolute",
        bottom: "5px",
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: CANVAS_Z_INDEX + 100000000,
      }}
    >
      <div style={{ position: "relative" }}>
        {/* Add spinner animation when in processing stage */}
        {isProcessing && (
          <>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
              .spinner-ring {
                animation: spin 1.5s linear infinite;
              }
            `}</style>
            <svg
              width={size + 16} // Make the spinner slightly larger
              height={size + 16}
              className="spinner-ring"
              style={{
                position: "absolute",
                top: -8,
                left: -8,
              }}
            >
              <defs>
                <linearGradient
                  id="spinnerGradient"
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor="#FE6526" stopOpacity="1" />
                  <stop offset="100%" stopColor="#FE6526" stopOpacity="0.3" />
                </linearGradient>
              </defs>
              <circle
                cx={(size + 16) / 2}
                cy={(size + 16) / 2}
                r={radius + 8}
                stroke="url(#spinnerGradient)"
                strokeWidth={3}
                fill="none"
                strokeDasharray="10 15"
                strokeLinecap="round"
              />
            </svg>
          </>
        )}

        {/* Main progress donut */}
        <svg
          width={size}
          height={size}
          role="progressbar"
          aria-valuenow={value}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e6e6e6"
            strokeWidth={strokeWidth}
            fill={innerColor}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
          <text
            x="50%"
            y="50%"
            dominantBaseline="middle"
            textAnchor="middle"
            fontSize="12"
            fill="#1e1e2f"
            fontWeight="bold"
            fontFamily="'Inter', 'Helvetica', 'Arial', sans-serif"
          >
            {isProcessing ? "" : `${value}%`}
          </text>
        </svg>
      </div>
      <div className="mt-2 text-center">
        <p style={{ color: "white", fontSize: "1rem", fontWeight: 500 }}>
          {label}
        </p>
      </div>
    </div>
  );
};

// =========

type Props = {
  elevation?: number; // vertical angle in degrees
  azimuth?: number; // horizontal angle around Y in degrees
  radius?: number;
  intensity?: number;
  color?: string;
  showHelper?: boolean;
  showShadowHelper?: boolean;
};

export function PointLightWithControls({
  elevation = 45,
  azimuth = 45,
  radius = 10,
  intensity = 1,
  color = "white",
  showHelper = false,
  showShadowHelper = false,
}: Props) {
  const radians = (deg: number) => (deg * Math.PI) / 180;

  const position = useMemo(() => {
    const phi = radians(elevation);
    const theta = radians(azimuth);

    const x = radius * Math.cos(phi) * Math.sin(theta);
    const y = radius * Math.sin(phi);
    const z = radius * Math.cos(phi) * Math.cos(theta);

    return [x, y, z] as [number, number, number];
  }, [elevation, azimuth, radius]);

  const lightRef = useRef<THREE.PointLight>(null);

  // Show light helper
  // useHelper(showHelper && lightRef, THREE.PointLightHelper, 0.1, "hotpink");

  // useEffect(() => {
  //   if (showShadowHelper && lightRef.current?.shadow?.camera) {
  //     const camHelper = new THREE.CameraHelper(lightRef.current.shadow.camera);
  //     lightRef.current.add(camHelper);

  //     return () => {
  //       lightRef.current?.remove(camHelper);
  //     };
  //   }
  // }, [showShadowHelper]);

  return (
    <pointLight
      ref={lightRef}
      position={position}
      color={color}
      intensity={intensity}
      castShadow
      shadow-mapSize-width={2048}
      shadow-mapSize-height={2048}
      shadow-bias={-0.005}
      distance={100}
      decay={2}
    />
  );
}

// =======

// Preload helper component to load all HDRIs at the start
export function EnvironmentPreloader() {
  useEffect(() => {
    // Preload all environment maps
    const environments = [
      "/hdri/morning.hdr",
      "/hdri/noon.hdr",
      "/hdri/sunset.hdr",
      "/hdri/night.hdr",
    ];

    environments.forEach((env) => {
      useLoader.preload(RGBELoader, env);
    });
  }, []);

  return null;
}

export function ExposureControl() {
  const { gl } = useThree();

  // Get exposure value from Redux store
  const exposure = BuilderSelect.ui_exposure();

  React.useEffect(() => {
    if (gl && exposure !== undefined) {
      gl.toneMappingExposure = exposure;
    }
  }, [gl, exposure]);

  return null;
}

// Helper to convert Kelvin to RGB
export const kelvinToRGB = (kelvin: number): THREE.Color => {
  // Algorithm from: http://www.tannerhelland.com/4435/convert-temperature-rgb-algorithm-code/

  // Clamp temperature to acceptable range
  kelvin = Math.max(1000, Math.min(40000, kelvin)) / 100;

  let r, g, b;

  // Calculate red
  if (kelvin <= 66) {
    r = 255;
  } else {
    r = kelvin - 60;
    r = 329.698727446 * Math.pow(r, -0.1332047592);
    r = Math.max(0, Math.min(255, r));
  }

  // Calculate green
  if (kelvin <= 66) {
    g = kelvin;
    g = 99.4708025861 * Math.log(g) - 161.1195681661;
  } else {
    g = kelvin - 60;
    g = 288.1221695283 * Math.pow(g, -0.0755148492);
  }
  g = Math.max(0, Math.min(255, g));

  // Calculate blue
  if (kelvin >= 66) {
    b = 255;
  } else if (kelvin <= 19) {
    b = 0;
  } else {
    b = kelvin - 10;
    b = 138.5177312231 * Math.log(b) - 305.0447927307;
    b = Math.max(0, Math.min(255, b));
  }

  // Convert to Three.js color (expects values in range 0-1)
  return new THREE.Color(r / 255, g / 255, b / 255);
};

export function LightSource({ kelvin, intensity }) {
  const lightColor = React.useMemo(() => {
    const rgb = kelvinToRGB(kelvin);
    return new THREE.Color(rgb.r, rgb.g, rgb.b);
  }, [kelvin]);

  return (
    <>
      <pointLight
        position={[0, 1, 0]}
        color={lightColor}
        intensity={intensity}
        distance={6}
        decay={1.5}
        //    castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-bias={-0.005} // Added shadow bias to prevent shadow acne
      />

      {/* <PointLightWithControls
        elevation={45}
        azimuth={270}
        radius={5}
        intensity={90}
        color="white"
        showHelper
        showShadowHelper
      /> */}
    </>
  );
}

// Environment wrapper with its own suspense boundary
export function EnvironmentWrapper({ environmentPath, background, intensity = 1.0 }) {
  return (
    <Suspense fallback={null}>
      <Environment files={environmentPath} background={background} intensity={intensity}/>
    </Suspense>
  );
}

// Scene setup with environment and controls
export function Initenv({
  environmentPath,
  background,
  lightKelvin,
  lightIntensity,
  environmentIntensity
}) {
  return (
    <>
      <LightSource kelvin={lightKelvin} intensity={lightIntensity} />

      <EnvironmentWrapper
        environmentPath={environmentPath}
        background={background}
        intensity={environmentIntensity}
      />

      {/* ExposureControl now gets the value from Redux */}
      <ExposureControl />
    </>
  );
}

// ======

// Create styled components with @mui/material/styles
export const CanvasContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  transition: TRANSITION_PROPERTIES,
  // Height and width will be defined via sx prop
}));

export const BlurOverlay = styled(Box)(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  backdropFilter: "blur(8px)",
  zIndex: OVERLAY_Z_INDEX,
  opacity: 0,
  visibility: "hidden",
  pointerEvents: "none", // Initially disable pointer events
  transition: `opacity ${TRANSITION_DURATION} ${TRANSITION_TIMING}, visibility ${TRANSITION_DURATION} ${TRANSITION_TIMING}`,
}));

// UI Button components
export const ActionButton = styled("button")(({ theme, color }) => ({
  position: "absolute",
  zIndex: CANVAS_Z_INDEX + 1000000,
  padding: "8px 16px",
  border: "none",
  borderRadius: "6px",
  fontSize: "0.9rem",
  fontWeight: "bold",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
  transition: "all 0.2s ease",
  backgroundColor: color || "#3f51b5",
  color: "white",
  "&:hover": {
    transform: "scale(1.05)",
    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
  },
  "&:active": {
    transform: "scale(0.98)",
  },
  "&:disabled": {
    backgroundColor: "#cccccc",
    color: "#666666",
    cursor: "not-allowed",
    transform: "scale(1)",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
}));


export const DirectionalLightWithShadows = ({
  position = [5, 5, 5],
  intensity = 20,
  color = "#ffffff",
  shadowMapSize = 1024,
  shadowCameraBounds = 15,
  initialElevation = 1,
  initialAzimuthal = 4.3,
  showHelper = true,
  castShadow = true
}) => {
  const lightRef = useRef();
  const targetRef = useRef();
  
  // Only the essential controls: azimuthal, elevation, and intensity
  // const { 
  //   lightIntensity, 
  //   elevationAngle, 
  //   azimuthalAngle 
  // } = useControls({
  //   lightIntensity: { value: intensity, min: 0, max: 150, step: 0.1 },
  //   elevationAngle: { value: initialElevation, min: -Math.PI / 2, max: Math.PI / 2, step: 0.01 },
  //   azimuthalAngle: { value: initialAzimuthal, min: 0, max: Math.PI * 2, step: 0.01 }
  // });
  
  // Only add helpers when component is mounted and showHelper is true
  useEffect(() => {
    if (showHelper && lightRef.current) {
      // Create the helper manually instead of using useHelper
      const directionalHelper = new THREE.DirectionalLightHelper(
        lightRef.current,
        1,
        color
      );
      
      // Create shadow camera helper
      const shadowHelper = new THREE.CameraHelper(
        lightRef.current.shadow.camera
      );
      
      // Add helpers to the scene
      const scene = lightRef.current.parent;
      if (scene) {
        scene.add(directionalHelper);
        scene.add(shadowHelper);
        
        // Update the helpers on each frame
        const updateHelpers = () => {
          directionalHelper.update();
          shadowHelper.update();
        };
        
        // Add the update function to the scene's onBeforeRender
        const originalBeforeRender = scene.onBeforeRender || (() => {});
        scene.onBeforeRender = (...args) => {
          originalBeforeRender(...args);
          updateHelpers();
        };
        
        // Return cleanup function
        return () => {
          scene.remove(directionalHelper);
          scene.remove(shadowHelper);
          scene.onBeforeRender = originalBeforeRender;
        };
      }
    }
    
    return () => {}; // Empty cleanup if no helpers were created
  }, [showHelper, color, lightRef]);
  
  // Update light position based on elevation and azimuthal angles
  // This runs only when control values change, not on every frame
  useEffect(() => {
    if (lightRef.current) {
      // Calculate position from spherical coordinates (radius = 10)
      const radius = 10;
      const x = Math.sin(initialElevation) * Math.cos(initialAzimuthal) * radius;
      const z = Math.sin(initialElevation) * Math.sin(initialAzimuthal) * radius;
      const y = Math.cos(initialElevation) * radius;
      
      lightRef.current.position.set(x, y, z);
      
      // Ensure light is targeting the center
      if (targetRef.current) {
        lightRef.current.target = targetRef.current;
      }
      
      // Update intensity
      lightRef.current.intensity = intensity;
    }
  }, [initialElevation, initialAzimuthal, intensity]);
  
  // Configure shadow properties - using fixed values from your existing code
  useEffect(() => {
    if (lightRef.current) {
      const light = lightRef.current;
      
      // Update shadow properties
      light.castShadow = castShadow;
      light.shadow.mapSize.width = shadowMapSize;
      light.shadow.mapSize.height = shadowMapSize;
      
      // Set shadow camera bounds
      const d = shadowCameraBounds;
      light.shadow.camera.left = -d;
      light.shadow.camera.right = d;
      light.shadow.camera.top = d;
      light.shadow.camera.bottom = -d;
      
      // Set near and far planes
      light.shadow.camera.near = 0.1;
      light.shadow.camera.far = 50;
      
      // Use the existing bias values from your code
      light.shadow.bias = 0.0;
      light.shadow.normalBias = 0.15;
      
      // Update the shadow camera
      light.shadow.camera.updateProjectionMatrix();
    }
  }, [shadowMapSize, castShadow, shadowCameraBounds]);

  return (
    <>
      {/* Directional Light */}
      <directionalLight
        ref={lightRef}
        position={position}
        intensity={intensity}
        color={color}
        castShadow={castShadow}
      />
      
      {/* Target for the directional light */}
      <object3D ref={targetRef} position={[0, 0, 0]} />
    </>
  );
};

// Isolated LightingControlPanel Component
export const LightingControlPanel = ({
  showControls,
  currentHdr,
  handlePresetChange,
  localExposure,
  handleExposureChange,
  lightBulbIntensity,
  setLightBulbIntensity,
  lightKelvin,
  setLightKelvin,
  showBackground,
  setShowBackground,
  kelvinValueText,
}) => {
  if (!showControls) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "50px",
        right: "0",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        pointerEvents: "auto",
      }}
    >
      <Paper
        elevation={6}
        style={{
          padding: "12px",
          borderRadius: "8px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          width: "350px",
          maxWidth: "calc(100vw - 30px)",
          position: "relative",
          transform: "none",
          margin: 0,
        }}
      >
        <h4
          style={{
            marginTop: 0,
            marginBottom: "6px",
            color: "#0D908E",
            fontSize: "14px",
          }}
        >
          Lighting Adjustments
        </h4>

        {/* HDRI Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginBottom: "8px",
            position: "relative",
          }}
        >
          <button
            onClick={() => handlePresetChange("morning")}
            style={{
              background:
                currentHdr === "morning"
                  ? "rgba(13, 144, 142, 0.1)"
                  : "transparent",
              border: "none",
              borderRadius: "6px",
              padding: "5px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={currentHdr === "morning" ? "#0D908E" : "#666"}
            >
              <path d="M12 6c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1z" />
            </svg>
            <span
              style={{
                fontSize: "10px",
                marginTop: "2px",
                color: currentHdr === "morning" ? "#0D908E" : "#666",
              }}
            >
              Morning
            </span>
          </button>

          <button
            onClick={() => handlePresetChange("noon")}
            style={{
              background:
                currentHdr === "noon"
                  ? "rgba(13, 144, 142, 0.1)"
                  : "transparent",
              border: "none",
              borderRadius: "6px",
              padding: "5px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={currentHdr === "noon" ? "#0D908E" : "#666"}
            >
              <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1z" />
            </svg>
            <span
              style={{
                fontSize: "10px",
                marginTop: "2px",
                color: currentHdr === "noon" ? "#0D908E" : "#666",
              }}
            >
              Noon
            </span>
          </button>

          <button
            onClick={() => handlePresetChange("sunset")}
            style={{
              background:
                currentHdr === "sunset"
                  ? "rgba(13, 144, 142, 0.1)"
                  : "transparent",
              border: "none",
              borderRadius: "6px",
              padding: "5px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={currentHdr === "sunset" ? "#0D908E" : "#666"}
            >
              <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zm-8-5c-.55 0-1 .45-1 1v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1z" />
            </svg>
            <span
              style={{
                fontSize: "10px",
                marginTop: "2px",
                color: currentHdr === "sunset" ? "#0D908E" : "#666",
              }}
            >
              Sunset
            </span>
          </button>

          <button
            onClick={() => handlePresetChange("night")}
            style={{
              background:
                currentHdr === "night"
                  ? "rgba(13, 144, 142, 0.1)"
                  : "transparent",
              border: "none",
              borderRadius: "6px",
              padding: "5px",
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill={currentHdr === "night" ? "#0D908E" : "#666"}
            >
              <path d="M12.34 2.02C6.59 1.82 2 6.42 2 12c0 5.52 4.48 10 10 10 3.71 0 6.93-2.02 8.66-5.02-7.51-.25-12.09-8.43-8.32-14.96z" />
            </svg>
            <span
              style={{
                fontSize: "10px",
                marginTop: "2px",
                color: currentHdr === "night" ? "#0D908E" : "#666",
              }}
            >
              Night
            </span>
          </button>
        </div>

        {/* Exposure Slider */}
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <label
              style={{
                color: "#0D908E",
                width: "110px",
                fontSize: "11px",
              }}
            >
              Exposure
            </label>
            <div style={{ flex: 1 }}>
              <Slider
                aria-label="Exposure"
                min={0.01}
                max={2}
                step={0.01}
                value={localExposure}
                onChange={handleExposureChange}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => value.toFixed(1)}
                sx={{
                  color: "#0D908E",
                  height: 4,
                  "& .MuiSlider-thumb": {
                    height: 14,
                    width: 14,
                    color: "#0D908E",
                  },
                  "& .MuiSlider-track": {
                    color: "#0D908E",
                    height: 4,
                  },
                  "& .MuiSlider-rail": {
                    color: "rgba(13, 144, 142, 0.3)",
                    height: 4,
                  },
                }}
              />
            </div>
            <span
              style={{
                width: "35px",
                textAlign: "right",
                color: "#0D908E",
                fontSize: "11px",
              }}
            >
              {localExposure.toFixed(1)}
            </span>
          </div>
        </div>

        {/* Light Intensity Slider */}
        <div>
  <div
    style={{
      display: "flex",
      alignItems: "center",
    }}
  >
    <label
      style={{
        color: "#0D908E",
        width: "110px",
        fontSize: "11px",
      }}
    >
      Environment Intensity
    </label>
    <div style={{ flex: 1 }}>
      <Slider
        aria-label="Environment Intensity"
        min={0}
        max={5}
        step={0.1}
        value={lightBulbIntensity} // Or use a separate state if needed
        onChange={(e, newValue) => setLightBulbIntensity(newValue)}
        valueLabelDisplay="auto"
        valueLabelFormat={(value) => value.toFixed(1)}
        sx={{
          color: "#0D908E",
          height: 4,
          "& .MuiSlider-thumb": {
            height: 14,
            width: 14,
            color: "#0D908E",
          },
          "& .MuiSlider-track": {
            color: "#0D908E",
            height: 4,
          },
          "& .MuiSlider-rail": {
            color: "rgba(13, 144, 142, 0.3)",
            height: 4,
          },
        }}
      />
    </div>
    <span
      style={{
        width: "35px",
        textAlign: "right",
        color: "#0D908E",
        fontSize: "11px",
      }}
    >
      {lightBulbIntensity.toFixed(1)}
    </span>
  </div>
</div>
        {/* Temperature (Kelvin) Slider */}
        <div style={{ marginBottom: "6px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "2px",
            }}
          >
            <label
              style={{
                color: "#0D908E",
                width: "110px",
                fontSize: "11px",
              }}
            >
              Temperature (K)
            </label>
            <div style={{ flex: 1 }}>
              <Slider
                aria-label="Temperature (K)"
                min={1000}
                max={10000}
                step={10}
                value={lightKelvin}
                onChange={(e, newValue) => setLightKelvin(newValue)}
                valueLabelDisplay="auto"
                getAriaValueText={kelvinValueText}
                sx={{
                  color: "#0D908E",
                  height: 4,
                  "& .MuiSlider-thumb": {
                    height: 14,
                    width: 14,
                    color: "#0D908E",
                  },
                  "& .MuiSlider-track": {
                    color: "#0D908E",
                    height: 4,
                  },
                  "& .MuiSlider-rail": {
                    color: "rgba(13, 144, 142, 0.3)",
                    height: 4,
                  },
                  "& .MuiSlider-markLabel": {
                    color: "#666",
                    fontSize: "9px",
                  },
                }}
                marks={[
                  {
                    value: 1000,
                    label: "1000K",
                  },
                  {
                    value: 5500,
                    label: "5500K",
                  },
                  {
                    value: 10000,
                    label: "10000K",
                  },
                ]}
              />
            </div>
            <span
              style={{
                width: "35px",
                textAlign: "right",
                color: "#0D908E",
                fontSize: "11px",
              }}
            >
              {lightKelvin}
            </span>
          </div>
        </div>

        {/* Background Toggle */}
        {/* <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "5px",
          }}
        >
          <input
            type="checkbox"
            id="background-toggle"
            checked={showBackground}
            onChange={(e) => setShowBackground(e.target.checked)}
            style={{ marginRight: "5px" }}
          />
          <label
            htmlFor="background-toggle"
            style={{ color: "#0D908E", fontSize: "11px" }}
          >
            Show as background
          </label>
        </div> */}
      </Paper>
    </div>
  );
};

// Lighting control toggle button that shows/hides the control panel
export const LightingControlToggle = ({
  showControls,
  setShowControls,
  currentHdr,
  handlePresetChange,
  localExposure,
  handleExposureChange,
  lightBulbIntensity,
  setLightBulbIntensity,
  lightKelvin,
  setLightKelvin,
  showBackground,
  setShowBackground,
  kelvinValueText,
}) => {
  return (
    <div
      style={{
        position: "fixed",
        bottom: "35px",
        right: "180px",
        zIndex: 10,
      }}
    >
      {/* Environment Toggle Button */}
      <button
        onClick={() => setShowControls(!showControls)}
        style={{
          position: "absolute",
          bottom: "7px",
          right: "0",
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          border: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          zIndex: 12,
        }}
      >
        {/* Light bulb icon */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="gray">
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zM2 13h2c.55 0 1-.45 1-1s-.45-1-1-1H2c-.55 0-1 .45-1 1s.45 1 1 1zm18 0h2c.55 0 1-.45 1-1s-.45-1-1-1h-2c-.55 0-1 .45-1 1s.45 1 1 1zM11 2v2c0 .55.45 1 1 1s1-.45 1-1V2c0-.55-.45-1-1-1s-1 .45-1 1zm0 18v2c0 .55.45 1 1 1s1-.45 1-1v-2c0-.55-.45-1-1-1s-1 .45-1 1zM5.99 4.58c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0s.39-1.03 0-1.41L5.99 4.58zm12.37 12.37c-.39-.39-1.03-.39-1.41 0-.39.39-.39 1.03 0 1.41l1.06 1.06c.39.39 1.03.39 1.41 0 .39-.39.39-1.03 0-1.41l-1.06-1.06zm1.06-10.96c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06zM7.05 18.36c.39-.39.39-1.03 0-1.41-.39-.39-1.03-.39-1.41 0l-1.06 1.06c-.39.39-.39 1.03 0 1.41s1.03.39 1.41 0l1.06-1.06z" />
        </svg>

        {/* Text label below the button */}
        <span
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: "2.5px",
            fontSize: "10px",
            fontWeight: "500",
            color: "#F8F6F7",
            whiteSpace: "nowrap",
          }}
        >
          Lighting
        </span>
      </button>

      {/* Render the control panel if showControls is true */}
      <LightingControlPanel
        showControls={showControls}
        currentHdr={currentHdr}
        handlePresetChange={handlePresetChange}
        localExposure={localExposure}
        handleExposureChange={handleExposureChange}
        lightBulbIntensity={lightBulbIntensity}
        setLightBulbIntensity={setLightBulbIntensity}
        lightKelvin={lightKelvin}
        setLightKelvin={setLightKelvin}
        showBackground={showBackground}
        setShowBackground={setShowBackground}
        kelvinValueText={kelvinValueText}
      />
    </div>
  );
};
export const GridViewButton = ({ onClick, active = true, show = true }) => {
  if (!show) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: "82.5px",
        right: "70px", // Positioned to the left of close button (which is at right: 20px)
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        zIndex: CANVAS_Z_INDEX + 1000002, // Higher than other UI elements to ensure visibility
        transition: "transform 0.2s ease-in-out",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
      }}
    >
      <div
        style={{
          width: "30px",
          height: "30px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "6px",
        }}
      >
        <svg
          viewBox="0 0 100 100"
          width="30"
          height="30"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Outer circle */}
          <circle
            cx="50"
            cy="50"
            r="48"
            stroke="white"
            strokeWidth="4"
            fill="none"
          />

          {/* Grid lines */}
          <line
            x1="33.33"
            y1="10"
            x2="33.33"
            y2="90"
            stroke="white"
            strokeWidth="4"
          />
          <line
            x1="66.66"
            y1="10"
            x2="66.66"
            y2="90"
            stroke="white"
            strokeWidth="4"
          />
          <line
            x1="10"
            y1="33.33"
            x2="90"
            y2="33.33"
            stroke="white"
            strokeWidth="4"
          />
          <line
            x1="10"
            y1="66.66"
            x2="90"
            y2="66.66"
            stroke="white"
            strokeWidth="4"
          />

          {/* Diagonal slash if not active */}
          {!active && (
            <line
              x1="20"
              y1="20"
              x2="80"
              y2="80"
              stroke="white"
              strokeWidth="12"
              strokeLinecap="round"
            />
          )}
        </svg>
      </div>
    </div>
  );
};

export const RetakeButton = ({
  onClick,
  showButton = true,
  isTemplateSaving = false,
}) => {
  // Don't show the button if explicitly told not to, or if we're currently saving the template
  if (!showButton || isTemplateSaving) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: "15px", // Match SaveButton's bottom positioning
        left: "calc(50% - 100px)", // Position to the left of SaveButton
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        zIndex: 1000001,
      }}
      onClick={onClick}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        }}
      >
        {/* Refresh/Retake Icon */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#333"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 12a9 9 0 0 1-9 9c-4.97 0-9-4.03-9-9s4.03-9 9-9h3" />
          <path d="M17 6l4 -4l-4 -4" />
        </svg>
      </div>
      <span
        style={{
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          marginTop: "5px",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
        }}
      >
        Retake
      </span>
    </div>
  );
};
export const CaptureButton = ({ onClick }) => {
  return (
    <div
      style={{
        position: "absolute",
        bottom: "20px", // slightly higher
        left: "50%",
        transform: "translate(-50%, 0)", // full centering
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        zIndex: 1000001,
        transition: "transform 0.2s ease-in-out",
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translate(-50%, 0) scale(1.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translate(-50%, 0)";
      }}
    >
      <div
        style={{
          width: "30px", // smaller size
          height: "30px",
          borderRadius: "50%",
          backgroundColor: "transparent",
          border: "2px solid white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "6px",
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}
      >
        <div
          style={{
            width: "18px",
            height: "18px",
            borderRadius: "50%",
            backgroundColor: "white",
          }}
        />
      </div>
      <span
        style={{
          color: "white",
          fontSize: "12px", // smaller text
          fontWeight: "500",
          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
        }}
      >
        Capture
      </span>
    </div>
  );
};

// SaveButton with modal positioned above the save button
export const SaveButton = ({
  onClick,
  showButton,
  isTemplateSaving,
  retakeClick,
  sceneRef,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // "idle", "saving", "saved"
  const layoutId = BuilderSelect.project().projectId;

  // Determine if the button should be disabled
  const isButtonDisabled =
    isTemplateSaving ||
    saveStatus === "saving" ||
    saveStatus === "saved" ||
    showModal;

  if (!showButton) return null;

  const handleButtonClick = async () => {
    if (isButtonDisabled) return; // Prevent click if button is disabled

    // Set status to saving to show spinner
    setSaveStatus("saving");

    try {
      // Call the original onClick handler (which saves the template)
      await onClick();
      // Show "Saved" status briefly
      setSaveStatus("saved");

      // Then show the modal after a brief delay
      setTimeout(() => {
        setShowModal(true);
        // Reset save status after showing modal
        setSaveStatus("idle");
      }, 1000);
    } catch (error) {
      console.error("Error saving template:", error);
      setSaveStatus("idle");
    }
  };

  const handleGoToTemplate = () => {
    window.location.href = `/ai/designer/designs/${layoutId}`;
  };

  const handleCreateAnother = () => {
    setShowModal(false);
    // Reset state for taking another snapshot
    if (typeof retakeClick === "function") {
      retakeClick();
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        bottom: "15px",
        left: "calc(50% + 0px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        zIndex: 1000000001,
      }}
    >
      {/* Modal appears directly above the button in the same wrapper */}
      {showModal && (
        <div
          style={{
            position: "absolute",
            bottom: "90px", // Position above the button
            width: "400px",
            backgroundColor: "white",
            borderRadius: "12px",
            padding: "24px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
            textAlign: "center",
            transform: "translateX(-50%)", // Center the modal
            left: "50%",
            zIndex: 2000000000,
          }}
        >
          <h2 style={{ color: "#333", fontSize: "20px", margin: "0 0 16px 0" }}>
            Snapshot saved
          </h2>
          <p style={{ color: "#555", fontSize: "14px", margin: "0 0 24px 0" }}>
            You can either view it in your images, or stay here to generate more
            snapshots with this design by moving products or the camera angle.
          </p>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <button
              onClick={handleGoToTemplate}
              style={{
                flex: 1,
                padding: "10px 16px",
                borderRadius: "8px",
                border: "1px solid #ccc",
                backgroundColor: "white",
                color: "#F57C00",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Go to template
            </button>
            <button
              onClick={handleCreateAnother}
              style={{
                flex: 1,
                padding: "10px 16px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#F57C00",
                color: "white",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
              }}
            >
              Create another
            </button>
          </div>

          {/* Triangle pointer at the bottom of the modal */}
          <div
            style={{
              position: "absolute",
              width: 0,
              height: 0,
              bottom: "-8px",
              left: "50%",
              transform: "translateX(-50%)",
              borderLeft: "15px solid transparent",
              borderRight: "15px solid transparent",
              borderTop: "15px solid white",
            }}
          />
        </div>
      )}

      {/* Save Button */}
      <div
        style={{
          cursor: isButtonDisabled ? "default" : "pointer",
          opacity: isButtonDisabled ? 0.7 : 1,
          transition: "opacity 0.3s ease",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pointerEvents: isButtonDisabled ? "none" : "auto", // Completely disable interactions when button should be disabled
        }}
        onClick={handleButtonClick}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: "50%",
            backgroundColor: "#ffffff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
            position: "relative",
          }}
        >
          {saveStatus === "saving" ? (
            <>
              {/* Spinner animation */}
              <style>{`
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                  .save-spinner {
                    animation: spin 1.5s linear infinite;
                  }
                `}</style>
              <svg
                className="save-spinner"
                width="24"
                height="24"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  fill="none"
                  stroke="#FE6526"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="40 60"
                />
              </svg>
            </>
          ) : saveStatus === "saved" ? (
            // Checkmark icon for "saved" state
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#4CAF50"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          ) : (
            // Normal save icon
            <SaveIcon style={{ fontSize: 30, color: "#333" }} />
          )}
        </div>
        <span
          style={{
            color: "white",
            fontSize: "12px",
            fontWeight: "500",
            marginTop: "5px",
            textShadow: "0 1px 2px rgba(0,0,0,0.5)",
          }}
        >
          {saveStatus === "saving"
            ? "Saving..."
            : saveStatus === "saved"
            ? "Saved!"
            : "Save snapshot"}
        </span>
      </div>
    </div>
  );
};

// ======

export const CameraViewfinder = ({
  lineThickness = 1,
  plusThickness = 1,
  cornerThickness = 4,
  cornerLength = 40,
  hideGridAndCrosshair = false, // This will control both grid lines and the center crosshair
  crosshairSize = 40, // Size parameter for the crosshair
}) => {
  const wrapperStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    pointerEvents: "none",
    zIndex: 100000,
  };

  const lineStyle = (thickness) => ({
    position: "absolute",
    backgroundColor: "rgba(255, 255, 255, 1.0)",
  });

  const horizontalLine = {
    ...lineStyle(lineThickness),
    height: `${lineThickness}px`,
    width: "100%",
    top: "33.33%",
  };

  const horizontalLine2 = {
    ...lineStyle(lineThickness),
    height: `${lineThickness}px`,
    width: "100%",
    top: "66.66%",
  };

  const verticalLine = {
    ...lineStyle(lineThickness),
    width: `${lineThickness}px`,
    height: "100%",
    left: "33.33%",
  };

  const verticalLine2 = {
    ...lineStyle(lineThickness),
    width: `${lineThickness}px`,
    height: "100%",
    left: "66.66%",
  };

  // Updated crosshair container
  const crossHairStyle = {
    position: "absolute",
    width: `${crosshairSize}px`,
    height: `${crosshairSize}px`,
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  };

  // Updated plus lines to use the crosshair size
  const plusLineH = {
    ...lineStyle(plusThickness),
    width: `${crosshairSize}px`, // Use full crosshair width
    height: `${plusThickness}px`,
    top: "50%",
    left: "0",
    transform: `translateY(-${plusThickness / 2}px)`,
  };

  const plusLineV = {
    ...lineStyle(plusThickness),
    width: `${plusThickness}px`,
    height: `${crosshairSize}px`, // Use full crosshair height
    top: "0",
    left: "50%",
    transform: `translateX(-${plusThickness / 2}px)`,
  };

  const renderCorner = (position) => {
    // Corner code remains unchanged
    const commonStyle = { position: "absolute" };

    const baseLineH = {
      ...lineStyle(cornerThickness),
      width: `${cornerLength}px`,
      height: `${cornerThickness}px`,
      position: "absolute",
    };

    const baseLineV = {
      ...lineStyle(cornerThickness),
      width: `${cornerThickness}px`,
      height: `${cornerLength}px`,
      position: "absolute",
    };

    switch (position) {
      case "top-left":
        return (
          <div style={{ ...commonStyle, top: 0, left: 0 }}>
            <div style={{ ...baseLineH, top: 0, left: 0 }} />
            <div style={{ ...baseLineV, top: 0, left: 0 }} />
          </div>
        );
      case "top-right":
        return (
          <div style={{ ...commonStyle, top: 0, right: 0 }}>
            <div style={{ ...baseLineH, top: 0, right: 0 }} />
            <div style={{ ...baseLineV, top: 0, right: 0 }} />
          </div>
        );
      case "bottom-left":
        return (
          <div style={{ ...commonStyle, bottom: 0, left: 0 }}>
            <div style={{ ...baseLineH, bottom: 0, left: 0 }} />
            <div style={{ ...baseLineV, bottom: 0, left: 0 }} />
          </div>
        );
      case "bottom-right":
        return (
          <div style={{ ...commonStyle, bottom: 0, right: 0 }}>
            <div style={{ ...baseLineH, bottom: 0, right: 0 }} />
            <div style={{ ...baseLineV, bottom: 0, right: 0 }} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div style={wrapperStyle}>
      {/* Grid lines and Crosshair - hide both based on hideGridAndCrosshair */}
      {!hideGridAndCrosshair && (
        <>
          {/* Grid lines */}
          <div style={horizontalLine}></div>
          <div style={horizontalLine2}></div>
          <div style={verticalLine}></div>
          <div style={verticalLine2}></div>

          {/* Crosshair */}
          <div style={crossHairStyle}>
            <div style={plusLineH}></div>
            <div style={plusLineV}></div>
          </div>
        </>
      )}

      {/* Corners - always visible */}
      {renderCorner("top-left")}
      {renderCorner("top-right")}
      {renderCorner("bottom-left")}
      {renderCorner("bottom-right")}
    </div>
  );
};

// Status panel component
export const StatusPanel = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: "20px",
  top: "130px",
  zIndex: CANVAS_Z_INDEX + 1000000,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  color: "black",
  padding: "12px 16px",
  borderRadius: "8px",
  fontSize: "0.9rem",
  fontFamily: "monospace",
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  boxShadow: "0 2px 15px rgba(0,0,0,0.15)",
  transition: "opacity 0.3s ease",
}));

// REFACTORED: Created separate component for canvas transition management
export const TransitionManager = ({
  isActive,
  canvasRef,
  overlayRef,
  controlsRef,
}) => {
  useEffect(() => {
    const canvasContainer = canvasRef.current;
    const blurOverlay = overlayRef.current;

    if (!canvasContainer || !blurOverlay) return;

    if (isActive) {
      // Show blur overlay
      blurOverlay.style.visibility = "visible";
      blurOverlay.style.opacity = "1";
      blurOverlay.style.pointerEvents = "auto"; // Enable pointer events for overlay
      blurOverlay.style.zIndex = CANVAS_Z_INDEX + 100000;

      // Animate canvas container
      canvasContainer.style.transform = `scale(${CANVAS_SCALE_FACTOR}) translateY(${CANVAS_TRANSLATE_Y})`;
      canvasContainer.style.zIndex = CANVAS_Z_INDEX + 1000000;
      canvasContainer.style.boxShadow = "0 22px 70px 4px rgba(0, 0, 0, 0.56)";
      canvasContainer.style.borderRadius = "12px";
    } else {
      // Hide blur overlay and completely disable pointer events
      blurOverlay.style.opacity = "0";
      blurOverlay.style.pointerEvents = "none"; // Disable pointer events immediately

      setTimeout(() => {
        blurOverlay.style.visibility = "hidden";
      }, parseFloat(TRANSITION_DURATION) * 1000); // Convert to milliseconds

      // Reset canvas container
      canvasContainer.style.transform = "scale(1) translateY(0)";
      canvasContainer.style.zIndex = "10";
      canvasContainer.style.boxShadow = "none";
      canvasContainer.style.borderRadius = "0";

      // Ensure controls are enabled
      if (controlsRef.current) {
        setTimeout(() => {
          controlsRef.current.enabled = true;
        }, 100);
      }
    }
  }, [isActive, canvasRef, overlayRef, controlsRef]);

  return null; // Purely effect-based component
};

// Patch the WebGLPathTracer when it's loaded
export const patchPathtracer = (module) => {
  try {
    // Save original PathTracingRenderer
    if (module.PathTracingRenderer) {
      const originalCompileMaterial =
        module.PathTracingRenderer.prototype.compileMaterial;

      // Replace the compileMaterial method with a version that handles missing compileAsync
      module.PathTracingRenderer.prototype.compileMaterial = function(
        material
      ) {
        // Ensure renderer has compileAsync
        if (this._renderer && !this._renderer.compileAsync) {
          this._renderer.compileAsync = function(scene, camera) {
            try {
              this.compile(scene, camera);
              return Promise.resolve();
            } catch (error) {
              return Promise.reject(error);
            }
          };
        }

        // Call original method
        return originalCompileMaterial.call(this, material);
      };

      console.log("Successfully patched PathTracingRenderer.compileMaterial");
    }
  } catch (error) {
    console.error("Error patching PathTracingRenderer:", error);
  }

  return module;
};

// Path traced scene component
// For PathtracedScene component
export const PathtracedScene = ({
  savePathtracerRef,
  infoRef,
  onSamplesUpdate,
  usePathtracerHook, // Add this prop
}) => {
  const { scene, camera } = useThree();
  const lastSamplesRef = useRef(0);

  // Only render the hook-using component if usePathtracerHook exists
  if (!usePathtracerHook) {
    return null; // Early return if hook doesn't exist
  }

  // Now we can safely use the hook unconditionally
  return (
    <PathtracedSceneInner
      savePathtracerRef={savePathtracerRef}
      infoRef={infoRef}
      onSamplesUpdate={onSamplesUpdate}
      usePathtracerHook={usePathtracerHook} // Pass it down
    />
  );
};

// For PathtracedSceneInner component
export const PathtracedSceneInner = ({
  savePathtracerRef,
  infoRef,
  onSamplesUpdate,
  usePathtracerHook, // Add this prop
}) => {
  // Now we can call the hook unconditionally
  const { pathtracer } = usePathtracerHook();
  const lastSamplesRef = useRef(0);

  // Update info panel with pathtracer status and save reference
  useFrame(() => {
    if (pathtracer) {
      try {
        // Calculate samples value
        const samplesValue = Math.ceil(pathtracer.samples) || 0;

        // Call the onSamplesUpdate callback if samples have changed
        if (samplesValue !== lastSamplesRef.current) {
          lastSamplesRef.current = samplesValue;
          if (onSamplesUpdate) {
            onSamplesUpdate(samplesValue);
          }
        }

        // Save pathtracer reference for snapshot functionality
        if (savePathtracerRef) {
          savePathtracerRef(pathtracer);
        }
      } catch (error) {
        console.warn("Error updating pathtracer info:", error);
      }
    }
  });

  return null; // The actual room contents are rendered by RoomViewRenderer
};

// Main scene content component with path tracer support
export const SceneContent = ({
  Pathtracer,
  usePathtracerHook,
  usePathtracer,
  saveGLContext,
  savePathtracerRef,
  infoRef,
  onSamplesUpdate,
  children,
}) => {
  const { gl, scene } = useThree();

  // Save the GL context
  useEffect(() => {
    if (gl && saveGLContext) {
      saveGLContext(gl);
    }
  }, [gl, scene, saveGLContext, usePathtracer]);

  // Render with pathtracer if enabled
  if (usePathtracer && Pathtracer) {
    return (
      <>
        {children}
        <Pathtracer
          minSamples={1}
          samples={SAMPLES_THRESHOLD}
          bounces={5}
          synchronizeRenderSize={true}
          renderDelay={10}
          dynamicLowRes={true}
          multipleImportanceSampling={true}
          filteredGlossyFactor={0.5} // 0.25
          tiles={3}
          renderScale={0.8} // 1 / window.devicePixelRatio
          resolutionFactor={1} // 0.8
          enabled={true}
        >
          <PathtracedScene
            savePathtracerRef={savePathtracerRef}
            infoRef={infoRef}
            onSamplesUpdate={onSamplesUpdate}
            usePathtracerHook={usePathtracerHook} // Pass the hook
          />
          {/* <Environment
            preset="studio"
            background // Optional, set as scene background
            backgroundBlurriness={0.85}
            backgroundIntensity={0.05}
          /> */}
        </Pathtracer>
      </>
    );
  }

  // Fallback render without pathtracer
  return <>{children}</>;
};

// Enhanced download handler component that works with pathtracer
export const EnhancedDownloadHandler = ({
  takeSnapshot,
  showSaveButton,
  retakeClick,
  isTemplateSaving,
  setIsTemplateSaving,
  sceneRef,
}) => {
  if (!showSaveButton) return null;

  return (
    <>
      {/* Always show RetakeButton alongside SaveButton, but pass isTemplateSaving to control visibility */}
      <RetakeButton
        onClick={retakeClick}
        showButton={true}
        isTemplateSaving={isTemplateSaving}
      />

      <SaveButton
        onClick={takeSnapshot}
        showButton={showSaveButton}
        isTemplateSaving={isTemplateSaving}
        setIsTemplateSaving={setIsTemplateSaving}
        retakeClick={retakeClick}
        sceneRef={sceneRef}
      />
    </>
  );
};
